/**
 * CheckersAI - AI opponent implementation with minimax algorithm
 *
 * Implements an AI checkers player with configurable difficulty levels (easy, medium, hard).
 * Uses minimax with alpha-beta pruning for move calculation and various evaluation
 * functions to assess board positions.
 *
 * Requirements: 10.1, 10.2, 10.3, 10.4, 11.1, 11.2, 11.3, 11.4, 12.1, 12.2, 12.3, 12.4, 12.5
 */

import {
  CheckersPieceGrid,
  CheckersDifficulty,
  CheckersMove,
  PlayerColor,
  Position,
} from './types';
import { CheckersEngine } from './CheckersEngine';

/**
 * Evaluation weights per difficulty level.
 *
 * | Factor                    | Easy | Medium | Hard |
 * |---------------------------|------|--------|------|
 * | Material (piece count)    | 1.0  | 0.60   | 0.40 |
 * | King count bonus          | 0    | 0.20   | 0.20 |
 * | Board control/advancement | 0    | 0.10   | 0.15 |
 * | Back-row defense          | 0    | 0.10   | 0.10 |
 * | Multi-jump potential      | 0    | 0      | 0.10 |
 * | King promotion proximity  | 0    | 0      | 0.05 |
 */
interface EvaluationWeights {
  material: number;
  kingBonus: number;
  boardControl: number;
  backRowDefense: number;
  multiJumpPotential: number;
  kingProximity: number;
}

const WEIGHTS: Record<CheckersDifficulty, EvaluationWeights> = {
  easy: {
    material: 1.0,
    kingBonus: 0,
    boardControl: 0,
    backRowDefense: 0,
    multiJumpPotential: 0,
    kingProximity: 0,
  },
  medium: {
    material: 0.60,
    kingBonus: 0.20,
    boardControl: 0.10,
    backRowDefense: 0.10,
    multiJumpPotential: 0,
    kingProximity: 0,
  },
  hard: {
    material: 0.40,
    kingBonus: 0.20,
    boardControl: 0.15,
    backRowDefense: 0.10,
    multiJumpPotential: 0.10,
    kingProximity: 0.05,
  },
};

/**
 * Piece base values used in material evaluation.
 */
const REGULAR_VALUE = 100;
const KING_VALUE = 150;

/**
 * CheckersAI class — AI opponent with evaluation functions and minimax search.
 */
export class CheckersAI {
  private difficulty: CheckersDifficulty;

  constructor(difficulty: CheckersDifficulty = 'medium') {
    this.difficulty = difficulty;
  }

  // ==========================================================================
  // Difficulty configuration
  // ==========================================================================

  /** Get the current difficulty. */
  getDifficulty(): CheckersDifficulty {
    return this.difficulty;
  }

  /** Set the difficulty level. */
  setDifficulty(difficulty: CheckersDifficulty): void {
    this.difficulty = difficulty;
  }

  /**
   * Get the minimax search depth for the current difficulty.
   *
   * Easy: 0 (random), Medium: 4, Hard: 8
   */
  getSearchDepth(): number {
    switch (this.difficulty) {
      case 'easy':
        return 0;
      case 'medium':
        return 4;
      case 'hard':
        return 8;
    }
  }

  /**
   * Determine if the AI should make a random (mistake) move.
   *
   * Easy: always random (100%), Medium: 0%, Hard: 0%
   */
  shouldMakeMistake(): boolean {
    return this.difficulty === 'easy';
  }

  // ==========================================================================
  // Evaluation functions
  // ==========================================================================

  /**
   * Evaluate material balance on the board.
   * Positive favours dark, negative favours light.
   *
   * Requirements: 10.1, 11.1, 12.1
   */
  evaluateMaterial(board: CheckersPieceGrid): number {
    let score = 0;
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = board[row]?.[col];
        if (!piece) continue;
        const value = piece.type === 'king' ? KING_VALUE : REGULAR_VALUE;
        score += piece.color === 'dark' ? value : -value;
      }
    }
    return score;
  }

  /**
   * Evaluate positional factors: piece advancement and board control.
   * Pieces closer to the opponent's king row score higher.
   *
   * Requirements: 11.2, 12.2
   */
  evaluatePositional(board: CheckersPieceGrid): number {
    let score = 0;
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = board[row]?.[col];
        if (!piece) continue;

        // Advancement: how far the piece has advanced toward the opponent's king row
        let advancement: number;
        if (piece.color === 'dark') {
          // Dark advances toward row 7
          advancement = row;
        } else {
          // Light advances toward row 0
          advancement = 7 - row;
        }

        // Center control bonus: pieces in the center columns (2-5) get a small bonus
        const centerBonus = (col >= 2 && col <= 5) ? 2 : 0;

        const positionalValue = advancement + centerBonus;
        score += piece.color === 'dark' ? positionalValue : -positionalValue;
      }
    }
    return score;
  }

  /**
   * Evaluate king safety for a given player.
   * Kings in the center of the board are generally safer and more mobile.
   *
   * Requirements: 11.1, 12.2
   */
  evaluateKingSafety(board: CheckersPieceGrid, player: PlayerColor): number {
    let safety = 0;
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = board[row]?.[col];
        if (!piece || piece.color !== player || piece.type !== 'king') continue;

        // Kings in the center are more mobile and harder to trap
        const rowCenter = Math.abs(row - 3.5);
        const colCenter = Math.abs(col - 3.5);
        const centralityBonus = 7 - (rowCenter + colCenter);

        // Edge penalty: kings on the edge have fewer escape routes
        const onEdge = row === 0 || row === 7 || col === 0 || col === 7;
        const edgePenalty = onEdge ? -3 : 0;

        safety += centralityBonus + edgePenalty;
      }
    }
    return safety;
  }

  /**
   * Evaluate back-row defense for a given player.
   * Keeping pieces on the back row prevents opponent kings from being created
   * via the back row and provides a defensive anchor.
   *
   * Requirements: 12.2
   */
  evaluateBackRowDefense(board: CheckersPieceGrid, player: PlayerColor): number {
    let defense = 0;
    const backRow = player === 'dark' ? 0 : 7;

    for (let col = 0; col < 8; col++) {
      const piece = board[backRow]?.[col];
      if (piece && piece.color === player) {
        defense += 5; // Bonus for each piece on the back row
      }
    }
    return defense;
  }

  /**
   * Evaluate multi-jump potential for a given player.
   * Counts how many pieces have jump moves available (proxy for tactical opportunities).
   */
  private evaluateMultiJumpPotential(board: CheckersPieceGrid, player: PlayerColor): number {
    // Create a temporary engine to check jump availability
    const tempEngine = new CheckersEngine();
    const state = tempEngine.getGameState();
    for (let r = 0; r < 8; r++) {
      for (let c = 0; c < 8; c++) {
        state.board[r][c] = board[r]?.[c] ?? null;
      }
    }

    let potential = 0;
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = board[row]?.[col];
        if (!piece || piece.color !== player) continue;
        const jumps = tempEngine.getJumpMoves({ row, col });
        potential += jumps.length;
      }
    }
    return potential;
  }

  /**
   * Evaluate king promotion proximity for a given player.
   * Regular pieces close to the king row score higher.
   */
  private evaluateKingProximity(board: CheckersPieceGrid, player: PlayerColor): number {
    let proximity = 0;
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = board[row]?.[col];
        if (!piece || piece.color !== player || piece.type !== 'regular') continue;

        // Distance to king row
        const distToKingRow = player === 'dark' ? (7 - row) : row;
        // Closer = higher score (max 7 when on row 0/7, but that would be a king already)
        proximity += (7 - distToKingRow);
      }
    }
    return proximity;
  }

  /**
   * Combined position evaluation using all evaluation functions.
   * Positive score favours dark, negative favours light.
   *
   * Requirements: 10.1, 11.1, 11.2, 12.1, 12.2
   */
  evaluatePosition(board: CheckersPieceGrid): number {
    const weights = WEIGHTS[this.difficulty];

    const material = this.evaluateMaterial(board);
    const positional = this.evaluatePositional(board);

    const darkKingSafety = this.evaluateKingSafety(board, 'dark');
    const lightKingSafety = this.evaluateKingSafety(board, 'light');
    const kingSafety = darkKingSafety - lightKingSafety;

    const darkBackRow = this.evaluateBackRowDefense(board, 'dark');
    const lightBackRow = this.evaluateBackRowDefense(board, 'light');
    const backRowDefense = darkBackRow - lightBackRow;

    const darkMultiJump = this.evaluateMultiJumpPotential(board, 'dark');
    const lightMultiJump = this.evaluateMultiJumpPotential(board, 'light');
    const multiJump = darkMultiJump - lightMultiJump;

    const darkKingProx = this.evaluateKingProximity(board, 'dark');
    const lightKingProx = this.evaluateKingProximity(board, 'light');
    const kingProximity = darkKingProx - lightKingProx;

    return (
      material * weights.material +
      positional * weights.boardControl +
      kingSafety * weights.kingBonus +
      backRowDefense * weights.backRowDefense +
      multiJump * weights.multiJumpPotential +
      kingProximity * weights.kingProximity
    );
  }

  // ==========================================================================
  // Move calculation
  // ==========================================================================

  /**
   * Calculate the best move for the current position.
   *
   * - Easy: random legal move, ≤500ms
   * - Medium: minimax depth 4, ≤1s
   * - Hard: minimax depth 8 with alpha-beta pruning, ≤2s
   *
   * Requirements: 10.1, 10.2, 10.3, 10.4, 11.1, 11.2, 11.3, 11.4, 12.1–12.5
   */
  calculateBestMove(engine: CheckersEngine, timeLimit: number): CheckersMove | null {
    const currentPlayer = engine.getCurrentPlayer();
    const allMoves = engine.getAllLegalMoves(currentPlayer);

    if (allMoves.length === 0) {
      return null;
    }

    // Easy difficulty: pick a random legal move
    if (this.shouldMakeMistake()) {
      return allMoves[Math.floor(Math.random() * allMoves.length)];
    }

    const depth = this.getSearchDepth();
    const startTime = Date.now();
    const deadline = startTime + timeLimit;

    let bestMove = allMoves[0];
    let bestScore = -Infinity;

    for (const move of allMoves) {
      // Time check
      if (Date.now() >= deadline) {
        break;
      }

      // Simulate the move on a cloned engine
      const clonedEngine = this.cloneEngine(engine);
      const result = clonedEngine.makeMove(move.from, move.to);

      if (!result.success) {
        continue;
      }

      // If the move results in a multi-jump continuation, complete it
      this.completeMultiJump(clonedEngine, move);

      // Evaluate with minimax (opponent is minimising)
      const score = this.minimax(
        clonedEngine,
        depth - 1,
        -Infinity,
        Infinity,
        false, // next player is opponent (minimising)
        deadline
      );

      if (score > bestScore) {
        bestScore = score;
        bestMove = move;
      }
    }

    return bestMove;
  }

  /**
   * Minimax algorithm with alpha-beta pruning.
   *
   * @param engine  Engine with current state
   * @param depth   Remaining search depth
   * @param alpha   Alpha bound
   * @param beta    Beta bound
   * @param maximizing  True if maximising player (dark)
   * @param deadline  Timestamp deadline for time limit
   * @returns Evaluation score
   */
  private minimax(
    engine: CheckersEngine,
    depth: number,
    alpha: number,
    beta: number,
    maximizing: boolean,
    deadline: number
  ): number {
    // Base cases
    if (depth <= 0 || Date.now() >= deadline) {
      return this.evaluatePosition(engine.getBoard());
    }

    const status = engine.getGameStatus();
    if (status.type === 'win') {
      // Large score for wins, adjusted by depth for faster mates
      if (status.winner === 'dark') return 10000 + depth;
      return -10000 - depth;
    }
    if (status.type === 'draw' || status.type === 'draw-agreement') {
      return 0;
    }

    const currentPlayer = engine.getCurrentPlayer();
    const allMoves = engine.getAllLegalMoves(currentPlayer);

    if (allMoves.length === 0) {
      // No moves = loss for current player
      return maximizing ? -10000 - depth : 10000 + depth;
    }

    if (maximizing) {
      let maxScore = -Infinity;
      for (const move of allMoves) {
        if (Date.now() >= deadline) break;

        const cloned = this.cloneEngine(engine);
        const result = cloned.makeMove(move.from, move.to);
        if (!result.success) continue;

        this.completeMultiJump(cloned, move);

        const score = this.minimax(cloned, depth - 1, alpha, beta, false, deadline);
        maxScore = Math.max(maxScore, score);
        alpha = Math.max(alpha, score);
        if (beta <= alpha) break; // Beta cutoff
      }
      return maxScore;
    } else {
      let minScore = Infinity;
      for (const move of allMoves) {
        if (Date.now() >= deadline) break;

        const cloned = this.cloneEngine(engine);
        const result = cloned.makeMove(move.from, move.to);
        if (!result.success) continue;

        this.completeMultiJump(cloned, move);

        const score = this.minimax(cloned, depth - 1, alpha, beta, true, deadline);
        minScore = Math.min(minScore, score);
        beta = Math.min(beta, score);
        if (beta <= alpha) break; // Alpha cutoff
      }
      return minScore;
    }
  }

  // ==========================================================================
  // Helpers
  // ==========================================================================

  /**
   * Clone a CheckersEngine by serializing and deserializing its state.
   */
  private cloneEngine(engine: CheckersEngine): CheckersEngine {
    const cloned = new CheckersEngine();
    const state = engine.getGameState();

    // Deep-copy the game state
    const serialized = JSON.parse(JSON.stringify(state));
    cloned.setGameState(serialized);
    return cloned;
  }

  /**
   * If a move results in a multi-jump continuation, automatically complete
   * the best continuation sequence (greedy: pick the first available jump
   * each time). This ensures the engine state is consistent after the move.
   */
  private completeMultiJump(engine: CheckersEngine, move: CheckersMove): void {
    // After the initial move, check if multi-jump is in progress
    let multiJumpPiece = engine.getMultiJumpPiece();
    while (multiJumpPiece && engine.isMultiJumpInProgress()) {
      const jumpMoves = engine.getJumpMoves(multiJumpPiece);
      if (jumpMoves.length === 0) break;

      // Pick the first available continuation jump
      const nextJump = jumpMoves[0];
      const result = engine.makeMove(multiJumpPiece, nextJump);
      if (!result.success) break;

      multiJumpPiece = engine.getMultiJumpPiece();
    }
  }
}
